import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import { formatBaseLabel } from "../../../utils/baseDisplay";
import { authenticatedFetch } from "../../../auth/auth";

function apiBase() {
  return (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).replace("T", " ").slice(0, 16);
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getChannelLabel(channel) {
  return channel === "chatcenter" ? "WhatsApp" : "E-mail";
}

function getStatusMeta(status) {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "sent") {
    return {
      label: "Enviado",
      hint: "Envio concluído com sucesso.",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    };
  }

  if (normalized === "opened") {
    return {
      label: "Visualizado",
      hint: "O cliente já abriu a mensagem.",
      className: "bg-sky-50 text-sky-700 border-sky-200",
    };
  }

  if (normalized === "error") {
    return {
      label: "Erro",
      hint: "Falha no envio da mensagem.",
      className: "bg-amber-50 text-amber-700 border-amber-200",
    };
  }

  if (normalized === "queued") {
    return {
      label: "Na fila",
      hint: "Aguardando processamento.",
      className: "bg-violet-50 text-violet-700 border-violet-200",
    };
  }

  return {
    label: normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "Sem status",
    hint: "Status retornado pelo sistema.",
    className: "bg-slate-50 text-slate-700 border-slate-200",
  };
}

function formatCurrency(value) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
}

function formatNumber(value, digits = 0) {
  return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(value || 0));
}

function formatPercent(value) {
  return `${formatNumber(value || 0, 1)}%`;
}

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function startOfMonthISO() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

function addDaysISO(days) {
  const date = new Date();
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function formatDateBR(value) {
  if (!value) return "---";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
}

function getClientStatusClass(status) {
  const key = String(status?.key || "").toLowerCase();
  if (key === "vermelho") return "bg-red-50 text-red-700 border-red-200";
  if (key === "amarelo") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
}


function extractHistoryReason(item) {
  if (!item) return "";

  const providerMessage = item?.providerResponse?.message;
  const providerDataMessage = item?.providerResponse?.data?.message;
  const providerError = item?.providerResponse?.error;
  const providerMessages = Array.isArray(item?.providerResponse?.message)
    ? item.providerResponse.message.join(", ")
    : "";

  const raw =
    item?.errorMessage ||
    item?.failureReason ||
    item?.lastError ||
    providerDataMessage ||
    providerMessage ||
    providerMessages ||
    providerError ||
    "";

  if (!raw) return "";

  const text = String(raw);

  if (/invalid_phone_number/i.test(text)) {
    return "Número inválido para envio no WhatsApp.";
  }

  if (/key should not be empty|mongodb id/i.test(text)) {
    return "Chave da ChatCenter inválida ou ausente.";
  }

  if (/channel_not_found/i.test(text)) {
    return "Canal do WhatsApp não encontrado na ChatCenter. Verifique se o número/canal está conectado e se a chave da API aponta para a conta correta.";
  }

  if (/recipient address rejected/i.test(text) && /limite de envio permitido por hora/i.test(text)) {
    return "Limite de envio por hora do e-mail atingido.";
  }

  if (/domain not found/i.test(text)) {
    return "Domínio do e-mail de destino não encontrado.";
  }

  return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}

function Modal({ open, title, children, onClose, maxWidthClass = "max-w-3xl" }) {
  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center px-4 pt-16 pb-4 md:px-6 md:pt-20 md:pb-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Modal"}
    >
      <div
        onClick={stop}
        className={`w-full ${maxWidthClass} bg-card rounded-lg border border-border card-shadow flex flex-col max-h-[82vh] md:max-h-[84vh] overflow-hidden`}
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 shrink-0">
          <div className="font-heading-semibold text-card-foreground">{title}</div>
          <button className="text-sm text-muted-foreground" onClick={onClose}>
            Fechar
          </button>
        </div>

        <div className="px-4 py-3 min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function CampaignSection({
  title,
  subtitle,
  rules,
  templates,
  loading,
  onPatchRule,
  onOpenTemplateEditor,
  templateById,
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="mb-3">
        <div className="text-sm font-body-medium text-card-foreground">{title}</div>
        <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
      </div>

      <div className="space-y-3">
        {rules.map((r) => {
          const tpl = templateById.get(r.templateId);
          return (
            <div key={r.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-sm font-body-medium text-card-foreground">{r.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Trigger: {r.trigger} • Offset: {r.offsetDays} dia(s) • Repetição: {Number(r.repeatEveryDays || 0)} • Empresa: {formatBaseLabel(r.base)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Template: {tpl?.name || r.templateId}</div>
                </div>

                <div className="flex items-center gap-2 flex-nowrap">
                  <button
                    className={`text-xs px-3 py-1 rounded-full border ${
                      r.enabled
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border text-muted-foreground"
                    }`}
                    onClick={() => onPatchRule(r.id, { enabled: !r.enabled })}
                    disabled={loading}
                  >
                    {r.enabled ? "Ativa" : "Desativada"}
                  </button>

                  <Button variant="outline" size="sm" onClick={() => onOpenTemplateEditor(r.templateId)} disabled={loading}>
                    Editar mensagem
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Empresa</div>
                  <select
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                    value={r.base || "ALL"}
                    onChange={(e) => onPatchRule(r.id, { base: e.target.value })}
                    disabled={loading}
                  >
                    <option value="ALL">{formatBaseLabel("ALL")}</option>
                    <option value="A">{formatBaseLabel("A")}</option>
                    <option value="B">{formatBaseLabel("B")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-muted-foreground mb-1">Template</div>
                  <select
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                    value={r.templateId || ""}
                    onChange={(e) => onPatchRule(r.id, { templateId: e.target.value })}
                    disabled={loading}
                  >
                    {(templates || []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-muted-foreground mb-1">Offset (dias)</div>
                  <input
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                    type="number"
                    value={Number(r.offsetDays ?? 0)}
                    onChange={(e) => onPatchRule(r.id, { offsetDays: Number(e.target.value) })}
                    disabled={loading}
                  />
                </div>

                <div>
                  <div className="text-xs text-muted-foreground mb-1">Repetição (dias)</div>
                  <input
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                    type="number"
                    value={Number(r.repeatEveryDays ?? 0)}
                    onChange={(e) => onPatchRule(r.id, { repeatEveryDays: Number(e.target.value) })}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {rules.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
            Nenhuma regra encontrada.
          </div>
        )}
      </div>
    </div>
  );
}


function DelinquencyAnalysisCard() {
  const [filters, setFilters] = useState({
    base: "ALL",
    de: startOfMonthISO(),
    ate: isoToday(),
    atrasoDe: addDaysISO(-180),
    diasOk: "7",
    diasCritico: "30",
  });
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadAnalysis() {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams(filters).toString();
      const res = await authenticatedFetch(`${apiBase()}/api/comercial/inadimplencia-analise?${qs}`, { cache: "no-store" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) {
        throw new Error(payload?.message || "Não foi possível carregar a análise de inadimplência.");
      }
      setAnalysis(payload.data || payload);
    } catch (err) {
      setError(err.message || "Não foi possível carregar a análise de inadimplência.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = analysis?.summary || {};
  const vendorMetrics = analysis?.vendorMetrics || [];
  const clientMetrics = analysis?.clientMetrics || [];
  const aging = summary.aging || {};

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-sm font-body-medium text-card-foreground">Análise de inadimplência</div>
          <div className="text-xs text-muted-foreground mt-1">
            Clientes, boletos em atraso, aging list, vendedor, rota, ticket médio da dívida e sinal verde/amarelo/vermelho.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadAnalysis} disabled={loading}>
          {loading ? "Atualizando..." : "Atualizar análise"}
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">Clientes em atraso</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatNumber(summary.clientesEmAtraso)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">Boletos em atraso</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatNumber(summary.boletosEmAtraso)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">Inadimplência</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatCurrency(summary.valorAberto)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">% vendas período</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatPercent(summary.inadimplenciaSobreVendas)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">Ticket médio dívida</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatCurrency(summary.ticketMedioDivida)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="text-[11px] uppercase text-muted-foreground">Prazo médio atraso</div>
          <div className="mt-2 text-xl font-heading-semibold text-card-foreground">{formatNumber(summary.prazoMedioAtraso, 1)} dias</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Empresa</div>
          <select className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" value={filters.base} onChange={(e) => setFilters((p) => ({ ...p, base: e.target.value }))}>
            <option value="ALL">{formatBaseLabel("ALL")}</option>
            <option value="A">{formatBaseLabel("A")}</option>
            <option value="B">{formatBaseLabel("B")}</option>
          </select>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Vendas de</div>
          <input className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" type="date" value={filters.de} onChange={(e) => setFilters((p) => ({ ...p, de: e.target.value }))} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Vendas até</div>
          <input className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" type="date" value={filters.ate} onChange={(e) => setFilters((p) => ({ ...p, ate: e.target.value }))} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Buscar atrasos desde</div>
          <input className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" type="date" value={filters.atrasoDe} onChange={(e) => setFilters((p) => ({ ...p, atrasoDe: e.target.value }))} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Verde até X dias</div>
          <input className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" type="number" value={filters.diasOk} onChange={(e) => setFilters((p) => ({ ...p, diasOk: e.target.value }))} />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Vermelho acima X dias</div>
          <input className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm" type="number" value={filters.diasCritico} onChange={(e) => setFilters((p) => ({ ...p, diasCritico: e.target.value }))} />
        </div>
      </div>

      {error ? <div className="mt-4 rounded-lg border border-border bg-accent/30 p-3 text-sm text-muted-foreground">{error}</div> : null}
      {(analysis?.warnings || []).length ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
          Avisos: {(analysis.warnings || []).slice(0, 3).map((item) => `${formatBaseLabel(item.base)}: ${item.message}`).join(" | ")}
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {["1-30", "31-60", "+60"].map((bucket) => (
          <div key={bucket} className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs font-body-medium text-card-foreground">Aging {bucket} dias</div>
            <div className="mt-2 text-sm text-muted-foreground">{formatNumber(aging[bucket]?.boletos || 0)} boleto(s)</div>
            <div className="text-sm font-body-medium text-card-foreground">{formatCurrency(aging[bucket]?.valor || 0)}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-3 py-2 border-b border-border text-sm font-body-medium text-card-foreground">Resumo por vendedor</div>
          <div className="overflow-auto max-h-[360px]">
            <table className="w-full text-xs min-w-[900px]">
              <thead className="text-muted-foreground uppercase bg-background sticky top-0">
                <tr className="border-b border-border">
                  <th className="text-left p-2">Vendedor</th>
                  <th className="text-right p-2">Clientes</th>
                  <th className="text-right p-2">Boletos</th>
                  <th className="text-right p-2">Valor aberto</th>
                  <th className="text-right p-2">Vendas período</th>
                  <th className="text-right p-2">% Inad.</th>
                  <th className="text-right p-2">Ticket dívida</th>
                  <th className="text-right p-2">Prazo médio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {vendorMetrics.map((row) => (
                  <tr key={row.vendedor}>
                    <td className="p-2 text-card-foreground">{row.vendedor}</td>
                    <td className="p-2 text-right">{formatNumber(row.clientesEmAtraso)}</td>
                    <td className="p-2 text-right">{formatNumber(row.boletosEmAtraso)}</td>
                    <td className="p-2 text-right">{formatCurrency(row.valorAberto)}</td>
                    <td className="p-2 text-right">{formatCurrency(row.valorVendidoPeriodo)}</td>
                    <td className="p-2 text-right">{formatPercent(row.inadimplenciaSobreVendas)}</td>
                    <td className="p-2 text-right">{formatCurrency(row.ticketMedioDivida)}</td>
                    <td className="p-2 text-right">{formatNumber(row.prazoMedioAtraso, 1)} dias</td>
                  </tr>
                ))}
                {!vendorMetrics.length ? <tr><td className="p-4 text-center text-muted-foreground" colSpan={8}>{loading ? "Carregando análise..." : "Nenhum vendedor com atraso no período."}</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="px-3 py-2 border-b border-border text-sm font-body-medium text-card-foreground">Clientes em atraso</div>
          <div className="overflow-auto max-h-[360px]">
            <table className="w-full text-xs min-w-[950px]">
              <thead className="text-muted-foreground uppercase bg-background sticky top-0">
                <tr className="border-b border-border">
                  <th className="text-left p-2">Sinal</th>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Vendedor</th>
                  <th className="text-left p-2">Rota</th>
                  <th className="text-right p-2">Boletos</th>
                  <th className="text-right p-2">Maior atraso</th>
                  <th className="text-right p-2">Prazo médio</th>
                  <th className="text-right p-2">Valor aberto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clientMetrics.map((row) => (
                  <tr key={row.id}>
                    <td className="p-2"><span className={`inline-flex px-2 py-1 rounded-full border whitespace-nowrap ${getClientStatusClass(row.status)}`}>{row.status?.label || "Verde"}</span></td>
                    <td className="p-2 text-card-foreground">{row.cliente}<div className="text-[11px] text-muted-foreground">{formatBaseLabel(row.base)} · {row.status?.motivo}</div></td>
                    <td className="p-2">{row.vendedor}</td>
                    <td className="p-2">{row.rota || "---"}</td>
                    <td className="p-2 text-right">{formatNumber(row.quantidadeBoletos)}</td>
                    <td className="p-2 text-right">{formatNumber(row.maxDiasAtraso)} dias</td>
                    <td className="p-2 text-right">{formatNumber(row.prazoMedioAtraso, 1)} dias</td>
                    <td className="p-2 text-right">{formatCurrency(row.valorAberto)}</td>
                  </tr>
                ))}
                {!clientMetrics.length ? <tr><td className="p-4 text-center text-muted-foreground" colSpan={8}>{loading ? "Carregando clientes..." : "Nenhum cliente em atraso encontrado."}</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ActiveCampaignsCard() {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [chatcenterRules, setChatcenterRules] = useState([]);
  const [chatcenterTemplates, setChatcenterTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyChannel, setHistoryChannel] = useState("all");

  const [error, setError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editChannel, setEditChannel] = useState("email");
  const [editTpl, setEditTpl] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editHtml, setEditHtml] = useState("");
  const [editText, setEditText] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const templateById = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((t) => m.set(t.id, t));
    return m;
  }, [templates]);

  const chatcenterTemplateById = useMemo(() => {
    const m = new Map();
    (chatcenterTemplates || []).forEach((t) => m.set(t.id, t));
    return m;
  }, [chatcenterTemplates]);

  async function loadAll(channel = historyChannel) {
    setLoading(true);
    setError("");

    try {
      const [cfgRes, histRes] = await Promise.all([
        authenticatedFetch(`${apiBase()}/api/email/config`, { cache: "no-store" }),
        authenticatedFetch(`${apiBase()}/api/email/history?limit=500&channel=${channel}`, { cache: "no-store" }),
      ]);

      const cfg = await cfgRes.json();
      const hist = await histRes.json();

      if (!cfg?.ok) throw new Error(cfg?.message || "Falha ao carregar config de campanhas.");
      if (!hist?.ok) throw new Error(hist?.message || "Falha ao carregar histórico de campanhas.");

      setRules(cfg.rules || []);
      setTemplates(cfg.templates || []);
      setChatcenterRules(cfg.chatcenterRules || []);
      setChatcenterTemplates(cfg.chatcenterTemplates || []);
      setHistory(hist.items || []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar dados de campanhas.");
      setRules([]);
      setTemplates([]);
      setChatcenterRules([]);
      setChatcenterTemplates([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll(historyChannel);
  }, [historyChannel]);

  async function patchRule(channel, ruleId, patch) {
    const path = channel === "chatcenter" ? "chatcenter/rules" : "rules";
    await authenticatedFetch(`${apiBase()}/api/email/${path}/${ruleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    await loadAll(historyChannel);
  }

  function openTemplateEditor(channel, templateId) {
    const map = channel === "chatcenter" ? chatcenterTemplateById : templateById;
    const tpl = map.get(templateId);
    if (!tpl) return;

    setEditChannel(channel);
    setEditTpl(tpl);
    setEditSubject(tpl.subject || "");
    setEditHtml(tpl.html || "");
    setEditText(tpl.text || "");
    setEditOpen(true);
  }

  async function saveTemplate() {
    if (!editTpl) return;

    const path = editChannel === "chatcenter" ? "chatcenter/templates" : "templates";
    const body = editChannel === "chatcenter"
      ? { subject: editSubject, text: editText }
      : { subject: editSubject, html: editHtml };

    await authenticatedFetch(`${apiBase()}/api/email/${path}/${editTpl.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    setEditOpen(false);
    setEditTpl(null);
    await loadAll(historyChannel);
  }

  const activeCount = rules.filter((r) => !!r.enabled).length + chatcenterRules.filter((r) => !!r.enabled).length;

  return (
    <div className="bg-card rounded-lg border border-border p-6 card-shadow">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading-semibold text-lg text-card-foreground">Campanhas Ativas</h3>
          <p className="text-sm text-muted-foreground">
            Gerenciamento de campanhas de cobrança automatizadas • {activeCount} ativa(s)
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} disabled={loading}>
            Ver histórico
          </Button>
          <Button variant="outline" size="sm" onClick={() => loadAll(historyChannel)} disabled={loading}>
            {loading ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <DelinquencyAnalysisCard />
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-border bg-accent/30 p-4 text-sm text-muted-foreground">
          {error}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <CampaignSection
            title="E-mail"
            subtitle="Modelo HTML enviado por SMTP, com link do boleto e tentativa de anexar o PDF."
            rules={rules}
            templates={templates}
            templateById={templateById}
            loading={loading}
            onPatchRule={(id, patch) => patchRule("email", id, patch)}
            onOpenTemplateEditor={(templateId) => openTemplateEditor("email", templateId)}
          />

          <CampaignSection
            title="WhatsApp via ChatCenter"
            subtitle="Modelo em texto simples enviado pela API da ChatCenter para o número do cliente."
            rules={chatcenterRules}
            templates={chatcenterTemplates}
            templateById={chatcenterTemplateById}
            loading={loading}
            onPatchRule={(id, patch) => patchRule("chatcenter", id, patch)}
            onOpenTemplateEditor={(templateId) => openTemplateEditor("chatcenter", templateId)}
          />
        </div>
      )}

      <Modal open={editOpen} title={`Editar mensagem • ${editTpl?.name || ""}`} onClose={() => setEditOpen(false)}>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Assunto / título</div>
            <input
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
            />
          </div>

          {editChannel === "chatcenter" ? (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Texto</div>
              <textarea
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm min-h-[220px]"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
              <div className="text-xs text-muted-foreground mt-2">
                Variáveis: {"{{cliente}} {{valor}} {{dias}} {{diasAtraso}} {{vencimento}} {{descricao}} {{empresa}} {{boletoUrl}} {{boletoLinha}}"}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs text-muted-foreground mb-1">HTML</div>
              <textarea
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm min-h-[220px]"
                value={editHtml}
                onChange={(e) => setEditHtml(e.target.value)}
              />
              <div className="text-xs text-muted-foreground mt-2">
                Variáveis: {"{{cliente}} {{valor}} {{dias}} {{diasAtraso}} {{vencimento}} {{descricao}} {{empresa}} {{boletoUrl}} {{boletoBlock}}"}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={saveTemplate}>
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={historyOpen} title="Histórico de envios" onClose={() => setHistoryOpen(false)} maxWidthClass="max-w-6xl">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground">Canal</div>
            <select
              className="rounded-md border border-border bg-card px-3 py-2 text-sm"
              value={historyChannel}
              onChange={(e) => setHistoryChannel(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="email">E-mail</option>
              <option value="chatcenter">WhatsApp</option>
            </select>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm min-w-[1100px]">
                <thead className="text-xs text-muted-foreground uppercase sticky top-0 bg-card z-10">
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3">Quando</th>
                    <th className="text-left py-2 px-3">Canal</th>
                    <th className="text-left py-2 px-3">Cliente</th>
                    <th className="text-left py-2 px-3">Destino</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Ações</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {(history || []).map((h) => {
                    const statusMeta = getStatusMeta(h.status);
                    return (
                      <tr key={h.messageId}>
                        <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{formatDateTime(h.sentAt || h.createdAt)}</td>
                        <td className="py-2 px-3 text-card-foreground">{getChannelLabel(h.channel || "email")}</td>
                        <td className="py-2 px-3 text-card-foreground">{h.clientName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{h.toEmail || h.toPhone || "-"}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex w-fit px-2.5 py-1 rounded-full border text-xs font-medium whitespace-nowrap ${statusMeta.className}`}>
                            {statusMeta.label}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2 flex-nowrap">
                            <button
                              type="button"
                              title="Ver conteúdo"
                              aria-label="Ver conteúdo"
                              className="inline-flex h-8.5 w-8.5 min-h-[34px] min-w-[34px] items-center justify-center rounded-full border border-border bg-background text-base hover:bg-accent/40 transition-colors shrink-0"
                              onClick={() => {
                                setPreviewItem(h);
                                setPreviewOpen(true);
                              }}
                            >
                              <span aria-hidden="true">👁️</span>
                            </button>

                            {h.boletoLink ? (
                              <a
                                title="Abrir boleto"
                                aria-label="Abrir boleto"
                                className="inline-flex h-8.5 w-8.5 min-h-[34px] min-w-[34px] items-center justify-center rounded-full border border-border bg-background text-base hover:bg-accent/40 transition-colors shrink-0"
                                href={h.boletoLink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <span aria-hidden="true">📄</span>
                              </a>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {history.length === 0 && (
                    <tr>
                      <td className="py-6 text-center text-muted-foreground" colSpan={6}>
                        Sem histórico ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={previewOpen} title="Conteúdo enviado" onClose={() => setPreviewOpen(false)} maxWidthClass="max-w-5xl">
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            Canal: <span className="text-card-foreground">{getChannelLabel(previewItem?.channel || "email")}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Status: <span className="text-card-foreground">{getStatusMeta(previewItem?.status).label}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Assunto / título: <span className="text-card-foreground">{previewItem?.subject || "-"}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Destino: <span className="text-card-foreground">{previewItem?.toEmail || previewItem?.toPhone || "-"}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Quando: <span className="text-card-foreground">{formatDateTime(previewItem?.sentAt || previewItem?.createdAt)}</span>
          </div>

          {extractHistoryReason(previewItem) ? (
            <div className="rounded-lg border border-border bg-background p-3 text-sm text-card-foreground">
              <span className="text-muted-foreground">Resumo do status: </span>
              {extractHistoryReason(previewItem)}
            </div>
          ) : null}

          {previewItem?.boletoLink ? (
            <div className="text-sm text-muted-foreground">
              Boleto:{" "}
              <a className="underline text-card-foreground" href={previewItem.boletoLink} target="_blank" rel="noreferrer">
                abrir
              </a>
            </div>
          ) : null}

          {(previewItem?.channel || "email") === "chatcenter" ? (
            <pre className="rounded-lg border border-border bg-background p-4 text-sm whitespace-pre-wrap break-words text-card-foreground">
              {previewItem?.messageText || "Sem texto encontrado"}
            </pre>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <iframe
                title="preview"
                className="w-full h-[520px] bg-white"
                srcDoc={previewItem?.emailHtml || "<div style='padding:20px;font-family:Arial'>Sem HTML encontrado</div>"}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
